/**
 * KPI card — the shared display surface for the Home "Your stats" strip.
 *
 * Token-compliant (no hardcoded colors), WCAG-safe status accents, tabular
 * numerals, optional sparkline, trend arrow, and a keyboard-accessible action
 * (open the detail popover). Supports two densities (comfortable / compact),
 * staggered entrance choreography (reduced-motion aware), and native
 * drag-to-reorder when the parent supplies drag handlers.
 */
import React from 'react';
import {
  type KpiMeta,
  type KpiPoint,
  STATUS_LABEL,
  evaluateStatus,
  formatKpiValue,
  statusColorVar,
  latestValue,
  seriesValues,
  seriesTrend,
  domainLabel,
} from './kpiFormat';
import { KpiSparkline } from './KpiSparkline';

export type KpiDensity = 'comfortable' | 'compact';

export interface KpiCardProps {
  meta: KpiMeta;
  points?: KpiPoint[];
  loading?: boolean;
  reducedMotion?: boolean;
  density?: KpiDensity;
  /** Staggered entrance delay (ms). Ignored when reducedMotion. */
  enterDelayMs?: number;
  /** Opens the detail popover when the card is activated. */
  onOpenDetail?: (id: string) => void;
  /** Renders an unpin (×) control in the top-right when provided. */
  onUnpin?: (id: string) => void;
  onUnpinLabel?: string;
  // --- drag-to-reorder (native HTML5 DnD; parent owns order state) ---
  draggable?: boolean;
  dragging?: boolean;
  /** Show an insertion indicator before this card (drop target). */
  dropBefore?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragEnter?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
}

export const KpiCard: React.FC<KpiCardProps> = ({
  meta,
  points,
  loading,
  reducedMotion,
  density = 'comfortable',
  enterDelayMs = 0,
  onOpenDetail,
  onUnpin,
  onUnpinLabel = 'Unpin',
  draggable,
  dragging,
  dropBefore,
  onDragStart,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
  onDragEnd,
}) => {
  const value = latestValue(points);
  const status = evaluateStatus(value, meta);
  const color = statusColorVar(status);
  const values = seriesValues(points);
  const trend = seriesTrend(points);
  const [hover, setHover] = React.useState(false);
  const [entered, setEntered] = React.useState(!!reducedMotion);
  const compact = density === 'compact';

  React.useEffect(() => {
    if (reducedMotion) {
      setEntered(true);
      return;
    }
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [reducedMotion]);

  const interactive = !!onOpenDetail;
  const open = () => onOpenDetail?.(meta.id);

  const trendGlyph = trend === 'up' ? '\u2197' : trend === 'down' ? '\u2198' : '\u2192';
  const displayValue = loading && value == null ? '\u2014' : formatKpiValue(value, meta.format, meta.unit);

  const entranceTransform = entered ? 'translateY(0)' : 'translateY(8px)';
  const hoverLift = hover && interactive && !reducedMotion && !dragging ? 'translateY(-2px)' : entranceTransform;

  return (
    <div
      data-testid={`kpi-card-${meta.id}`}
      data-status={status}
      data-density={density}
      role={interactive ? 'button' : 'group'}
      aria-label={`${meta.title}: ${displayValue}${status !== 'none' ? `, ${STATUS_LABEL[status]}` : ''}`}
      tabIndex={interactive ? 0 : undefined}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={interactive ? open : undefined}
      onKeyDown={(e) => {
        if (interactive && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          open();
        }
      }}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? 4 : 8,
        padding: compact ? '10px 12px 10px 14px' : '14px 16px',
        background: 'var(--card-bg)',
        border: dropBefore
          ? '1px solid var(--main-accent)'
          : '1px solid var(--card-border, var(--input-border))',
        borderRadius: 12,
        cursor: interactive ? 'pointer' : 'default',
        overflow: 'hidden',
        opacity: entered ? (dragging ? 0.45 : 1) : 0,
        transform: hoverLift,
        boxShadow:
          hover && interactive && !dragging
            ? `0 8px 24px color-mix(in srgb, ${color} 22%, transparent)`
            : 'none',
        boxSizing: 'border-box',
        transition: reducedMotion
          ? 'none'
          : `opacity 320ms ease ${enterDelayMs}ms, transform 320ms cubic-bezier(0.16,1,0.3,1) ${entered ? 0 : enterDelayMs}ms, box-shadow 160ms`,
      }}
    >
      {/* Drop insertion indicator */}
      {dropBefore && (
        <span aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, top: -1, height: 3, background: 'var(--main-accent)', borderRadius: 3 }} />
      )}

      {/* Status rail */}
      <span
        aria-hidden="true"
        style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: color, opacity: status === 'none' ? 0.35 : 0.9 }}
      />

      {/* Header: (grip) + title + domain + optional unpin */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, minWidth: 0 }}>
          {draggable && (
            <span
              aria-hidden="true"
              title="Drag to reorder"
              style={{ flexShrink: 0, marginTop: 1, cursor: 'grab', color: 'var(--main-text-secondary)', opacity: hover ? 0.9 : 0.45, transition: reducedMotion ? 'none' : 'opacity 120ms', lineHeight: 0 }}
            >
              <GripGlyph />
            </span>
          )}
          <div style={{ minWidth: 0 }}>
            <div
              title={meta.description || meta.title}
              style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--main-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {meta.title}
            </div>
            {!compact && (
              <div style={{ fontSize: 10.5, color: 'var(--main-text-secondary)', marginTop: 1 }}>
                {domainLabel(meta.domain)}
              </div>
            )}
          </div>
        </div>
        {onUnpin && (
          <button
            type="button"
            data-testid={`kpi-unpin-${meta.id}`}
            aria-label={`${onUnpinLabel} ${meta.title}`}
            title={onUnpinLabel}
            draggable={false}
            onClick={(e) => {
              e.stopPropagation();
              onUnpin(meta.id);
            }}
            style={{
              flexShrink: 0, width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              padding: 0, lineHeight: 1, fontSize: 14, color: 'var(--main-text-secondary)', background: 'transparent',
              border: '1px solid transparent', borderRadius: 6, cursor: 'pointer', opacity: hover ? 1 : 0.55,
              transition: reducedMotion ? 'none' : 'opacity 120ms, background 120ms',
            }}
          >
            {'\u00d7'}
          </button>
        )}
      </div>

      {/* Value + trend */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span
          data-testid={`kpi-value-${meta.id}`}
          style={{ fontSize: compact ? 20 : 26, fontWeight: 700, lineHeight: 1.05, color: 'var(--main-text)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}
        >
          {displayValue}
        </span>
        {values.length >= 2 && (
          <span aria-hidden="true" style={{ fontSize: 12, fontWeight: 600, color, opacity: 0.9 }} title={`Trend: ${trend}`}>
            {trendGlyph}
          </span>
        )}
        {compact && (
          <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--main-text-secondary)' }}>{domainLabel(meta.domain)}</span>
        )}
      </div>

      {/* Sparkline (comfortable only) */}
      {!compact && (
        <div style={{ marginTop: 'auto' }}>
          <KpiSparkline values={values} color={color} ariaLabel={`${meta.title} trend`} />
        </div>
      )}
    </div>
  );
};

function GripGlyph() {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="9" cy="6" r="1.6" />
      <circle cx="15" cy="6" r="1.6" />
      <circle cx="9" cy="12" r="1.6" />
      <circle cx="15" cy="12" r="1.6" />
      <circle cx="9" cy="18" r="1.6" />
      <circle cx="15" cy="18" r="1.6" />
    </svg>
  );
}

export default KpiCard;
